import { useState } from 'react'
import { marked } from 'marked'
import { Copy, Check, Play, Pause, StopCircle, RotateCcw, Pencil, CornerDownRight } from 'lucide-react'
import { API_BASE_URL } from '../../lib/config'
import { formatChatDate } from '../../components/lib/dateUtils'
import '../../components/lib/markedChatConfig'
import EditMessageModal from './EditMessageModal'
import { SkillOfferCard } from './skills/SkillOfferCard'

const iconSize = 12
const iconStyle = { width: iconSize, height: iconSize, flexShrink: 0 }

function renderMarkdown(text: string): string {
  if (!text || !text.trim()) return ''
  const raw = marked.parse(text.trim())
  return typeof raw === 'string' ? raw : String(raw ?? '')
}

export interface RefFile {
  name: string
  ext: string
  messageId?: string
}

export interface RefAIData {
  h: string
  b: string
  pts: string[]
  a: string
  tags: string[]
  /** Assistant reply ended at provider max output tokens; show Continue. */
  truncatedMaxTokens?: boolean
}

export type RefMessage =
  | { role: 'user'; id: string; text?: string; files?: RefFile[]; timestamp?: Date }
  | {
      role: 'ai'
      id: string
      data: RefAIData
      timestamp?: Date
      skillOffer?: { skillId: string; skillTitle: string; active: boolean }
    }

interface MessageListProps {
  messages: RefMessage[]
  isTyping?: boolean
  userInitials?: string
  playingMessageId?: string | null
  isPaused?: boolean
  onPlayMessage?: (messageId: string, content: string) => void
  onStopPlayback?: () => void
  onRegenerate?: (messageId: string) => void
  lastAiMessageId?: string | null
  lastUserMessageId?: string | null
  onEditUserMessage?: (messageId: string, newContent: string) => Promise<void>
  regeneratingMessageId?: string | null
  /** Sends "continue" as the user to finish a max-token-truncated reply. */
  onContinueAssistant?: () => void
  onSkillOfferAccurate?: (offerMessageId: string, skillId: string) => void
  onSkillOfferQuick?: (offerMessageId: string, skillId: string) => void
}

const btnStyle = {
  padding: '4px 8px',
  fontSize: 11,
  fontWeight: 500,
  color: '#94A3B8',
  background: '#F5F6FA',
  border: '1px solid rgba(0,0,0,0.08)',
  borderRadius: 6,
  cursor: 'pointer' as const,
  transition: 'background .18s ease, border-color .18s ease, color .18s ease',
}

function UserMsg({
  id,
  text,
  files = [],
  initials = 'U',
  timestamp,
  isLastUserEditable,
  onEditUserMessage,
}: {
  id: string
  text?: string
  files?: RefFile[]
  initials?: string
  timestamp?: Date
  isLastUserEditable?: boolean
  onEditUserMessage?: (messageId: string, newContent: string) => Promise<void>
}) {
  const [editOpen, setEditOpen] = useState(false)

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'row-reverse',
        gap: 12,
        animation: 'msgIn .35s cubic-bezier(.16,1,.3,1) both',
      }}
    >
      <div
        style={{
          width: 30,
          height: 30,
          borderRadius: '50%',
          background: 'var(--bg2)',
          // border: '1.5px solid var(--bd)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 10,
          fontWeight: 600,
          color: 'var(--navy)',
          flexShrink: 0,
          marginTop: 2,
        }}
      >
        {initials}
      </div>
      <div style={{ flex: 1, maxWidth: 'calc(100% - 42px)' }}>
        <div
          style={{
            fontSize: 9.5,
            fontWeight: 500,
            letterSpacing: '1.5px',
            textTransform: 'uppercase',
            color: 'var(--muted)',
            marginBottom: 5,
            textAlign: 'right',
          }}
        >
          You
        </div>
        <div
          style={{
            background: '#EEEFF1',
            // border: '1px solid rgba(0,0,0,0.05)',
            borderRadius: 14,
            borderTopRightRadius: 4,
            padding: '13px 17px',
            fontSize: 13,
            lineHeight: 1.75,
            color: '#1E293B',
          }}
        >
          {files?.map((f, i) => (
            <div
              key={i}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 8,
                background: '#FFFFFF',
                border: '1px solid rgba(0,0,0,0.08)',
                borderRadius: 8,
                padding: '7px 11px',
                marginBottom: 7,
                fontSize: 11.5,
                color: '#1E293B',
              }}
            >
              <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span>
                  {f.ext === 'pdf'
                    ? '📄'
                    : ['png', 'jpg', 'jpeg'].includes(f.ext)
                      ? '🖼️'
                      : '📊'}
                </span>
                <span style={{ fontWeight: 500 }}>{f.name}</span>
              </span>
              {f.messageId && (
                <a
                  href={`${API_BASE_URL}chat/file/${f.messageId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    fontSize: 11,
                    color: 'var(--blue)',
                    fontWeight: 500,
                    textDecoration: 'none',
                  }}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ color: 'var(--navy)' }}>
                    <path d="M15 22.75H9C3.57 22.75 1.25 20.43 1.25 15V9C1.25 3.57 3.57 1.25 9 1.25H15C20.43 1.25 22.75 3.57 22.75 9V15C22.75 20.43 20.43 22.75 15 22.75ZM9 2.75C4.39 2.75 2.75 4.39 2.75 9V15C2.75 19.61 4.39 21.25 9 21.25H15C19.61 21.25 21.25 19.61 21.25 15V9C21.25 4.39 19.61 2.75 15 2.75H9Z" fill="currentColor"/>
                    <path d="M11.9999 15.26C11.8099 15.26 11.6199 15.1899 11.4699 15.0399L8.46994 12.0399C8.17994 11.7499 8.17994 11.27 8.46994 10.98C8.75994 10.69 9.23994 10.69 9.52994 10.98L11.9999 13.45L14.4699 10.98C14.7599 10.69 15.2399 10.69 15.5299 10.98C15.8199 11.27 15.8199 11.7499 15.5299 12.0399L12.5299 15.0399C12.3799 15.1899 12.1899 15.26 11.9999 15.26Z" fill="currentColor"/>
                    <path d="M12 15.26C11.59 15.26 11.25 14.92 11.25 14.51V6.51001C11.25 6.10001 11.59 5.76001 12 5.76001C12.41 5.76001 12.75 6.10001 12.75 6.51001V14.51C12.75 14.93 12.41 15.26 12 15.26Z" fill="currentColor"/>
                    <path d="M11.9999 18.2302C9.88994 18.2302 7.76995 17.8902 5.75995 17.2202C5.36995 17.0902 5.15995 16.6602 5.28995 16.2702C5.41995 15.8802 5.83994 15.6602 6.23994 15.8002C9.95994 17.0402 14.0499 17.0402 17.7699 15.8002C18.1599 15.6702 18.5899 15.8802 18.7199 16.2702C18.8499 16.6602 18.6399 17.0902 18.2499 17.2202C16.2299 17.9002 14.1099 18.2302 11.9999 18.2302Z" fill="currentColor"/>
                  </svg>
                </a>
              )}
            </div>
          ))}
          {text && (
            <div
              className="msg-markdown"
              style={{ color: '#1E293B' }}
              dangerouslySetInnerHTML={{ __html: renderMarkdown(text) }}
            />
          )}
          {isLastUserEditable && onEditUserMessage && (
            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
                marginTop: 10,
                paddingTop: 8,
                borderTop: '1px solid rgba(0,0,0,0.06)',
              }}
            >
              <button
                type="button"
                className="ai-msg-btn"
                onClick={() => setEditOpen(true)}
                style={{ ...btnStyle, display: 'inline-flex', alignItems: 'center', gap: 4 }}
              >
                <Pencil style={iconStyle} />
                <span>Edit</span>
              </button>
            </div>
          )}
        </div>
        {timestamp && (
          <span
            style={{
              display: 'block',
              fontSize: 10,
              color: 'var(--muted)',
              marginTop: 4,
              textAlign: 'right',
            }}
          >
            {formatChatDate(timestamp)}
          </span>
        )}
        <EditMessageModal
          open={editOpen}
          onOpenChange={setEditOpen}
          initialText={text ?? ''}
          onConfirm={async (newContent) => {
            if (!onEditUserMessage) return
            await onEditUserMessage(id, newContent)
          }}
        />
      </div>
    </div>
  )
}

function StreamingDots() {
  return (
    <div
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
        padding: '4px 0',
      }}
    >
      {[0, 0.2, 0.4].map((d, i) => (
        <span
          key={i}
          style={{
            width: 6,
            height: 6,
            background: 'var(--blue)',
            borderRadius: '50%',
            opacity: 0.3,
            animation: `dot 1.2s ease-in-out ${d}s infinite`,
          }}
        />
      ))}
    </div>
  )
}

function AIMsg({
  id: messageId,
  data,
  isPlaying,
  isPaused,
  onPlayMessage,
  onStopPlayback,
  onRegenerate,
  isLastAi,
  timestamp,
  isRegenerating,
  isStreaming,
  onContinueAssistant,
  skillOffer,
  onSkillOfferAccurate,
  onSkillOfferQuick,
}: {
  id: string
  data: RefAIData
  isPlaying?: boolean
  isPaused?: boolean
  onPlayMessage?: (messageId: string, content: string) => void
  onStopPlayback?: () => void
  onRegenerate?: (messageId: string) => void
  isLastAi?: boolean
  timestamp?: Date
  isRegenerating?: boolean
  isStreaming?: boolean
  onContinueAssistant?: () => void
  skillOffer?: { skillId: string; skillTitle: string; active: boolean }
  onSkillOfferAccurate?: (offerMessageId: string, skillId: string) => void
  onSkillOfferQuick?: (offerMessageId: string, skillId: string) => void
}) {
  const [copied, setCopied] = useState(false)
  
  // For the copy button, strip markdown formatting
  const plainTextContent = [data.b, ...data.pts]
    .filter(Boolean)
    .join('\n\n')
    // eslint-disable-next-line no-useless-escape -- [] must be escaped in this class for clarity
    .replace(/[#*`_~>\[\]]/g, '')

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(plainTextContent)
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch {
      // ignore
    }
  }

  const isOffer = !!skillOffer

  return (
    <div
      style={{
        display: 'flex',
        gap: 12,
        animation: 'msgIn .35s cubic-bezier(.16,1,.3,1) both',
      }}
    >
      <div
        style={{
          width: 30,
          height: 30,
          borderRadius: '50%',
          background: 'var(--navy)',
          fontFamily: "'Playfair Display',serif",
          fontSize: 14,
          color: 'var(--gold)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          marginTop: 2,
        }}
      >
        A
      </div>
      <div style={{ flex: 1, maxWidth: 'calc(100% - 42px)' }}>
        <div
          style={{
            fontSize: 9.5,
            fontWeight: 500,
            letterSpacing: '1.5px',
            textTransform: 'uppercase',
            color: 'var(--muted2)',
            marginBottom: 5,
          }}
        >
          Athena · Beta
        </div>
        <div
          style={{
            background: 'var(--white)',
            border: isOffer ? '1px solid var(--bd)' : undefined,
            borderRadius: 14,
            borderTopLeftRadius: 4,
            padding: isOffer ? '13px 17px' : undefined,
            fontSize: 13,
            lineHeight: 1.75,
            boxShadow: isOffer ? '0 1px 4px rgba(0,0,0,0.06)' : undefined,
          }}
        >
          {isOffer ? (
            <SkillOfferCard
              skillTitle={skillOffer.skillTitle}
              active={skillOffer.active}
              onAccurate={() => onSkillOfferAccurate?.(messageId, skillOffer.skillId)}
              onQuick={() => onSkillOfferQuick?.(messageId, skillOffer.skillId)}
            />
          ) : isRegenerating ? (
            <div
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                color: 'var(--muted2)',
              }}
            >
              {[0, 0.2, 0.4].map((d, i) => (
                <span
                  key={i}
                  style={{
                    width: 5,
                    height: 5,
                    background: 'var(--blue)',
                    borderRadius: '50%',
                    opacity: 0.5,
                    animation: `dot 1.2s ease-in-out ${d}s infinite`,
                  }}
                />
              ))}
              <span>Regenerating…</span>
            </div>
          ) : isStreaming && !data.b ? (
            <StreamingDots />
          ) : data.b ? (
            <div
              className="msg-markdown"
              style={{ marginBottom: 9, color: 'var(--text)' }}
              dangerouslySetInnerHTML={{ __html: renderMarkdown(data.b) }}
            />
          ) : null}
          {!isOffer && data.pts.length > 0 && (
            <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 9px' }}>
              {data.pts.map((p, i) => (
                <li
                  key={i}
                  style={{
                    display: 'flex',
                    gap: 8,
                    padding: '4px 0',
                    fontSize: 13,
                    lineHeight: 1.65,
                    borderBottom: '1px solid var(--bd2)',
                  }}
                >
                  <span
                    style={{
                      display: 'inline-block',
                      width: 2,
                      minWidth: 2,
                      height: 14,
                      background: 'var(--blue)',
                      borderRadius: 1,
                      opacity: 0.5,
                      marginTop: 4,
                      flexShrink: 0,
                    }}
                  />
                  {p}
                </li>
              ))}
            </ul>
          )}
          {!isOffer && data.a && (
            <div>
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 5,
                  background: 'var(--blue-lt)',
                  border: '1px solid var(--blue-bd)',
                  borderRadius: 7,
                  padding: '5px 12px',
                  fontSize: 11.5,
                  color: 'var(--blue)',
                  fontWeight: 500,
                  cursor: 'pointer',
                  marginTop: 11,
                }}
              >
                {data.a}
              </span>
            </div>
          )}
          {!isOffer && data.tags.length > 0 && (
            <div style={{ marginTop: 4 }}>
              {data.tags.map((t) => (
                <span
                  key={t}
                  style={{
                    display: 'inline-flex',
                    background: 'var(--bg)',
                    border: '1px solid var(--bd)',
                    borderRadius: 5,
                    padding: '2px 8px',
                    fontSize: 10,
                    color: 'var(--muted)',
                    marginRight: 4,
                    marginTop: 7,
                  }}
                >
                  {t}
                </span>
              ))}
            </div>
          )}
          <div
            style={{
              // display: 'flex',
              display: 'none',
              alignItems: 'flex-start',
              gap: 7,
              background: '#FFFBEB',
              border: '1px solid #FDE68A',
              borderRadius: 8,
              padding: '7px 11px',
              marginTop: 11,
              fontSize: 11,
              color: '#92400E',
              lineHeight: 1.5,
            }}
          >
            ⚠️ Athena can make mistakes. Verify important information with your coach.
          </div>
          {!isOffer &&
            isLastAi &&
            data.truncatedMaxTokens &&
            onContinueAssistant &&
            !isStreaming &&
            !isRegenerating && (
              <div
                className="justify-between"
                style={{
                  marginTop: 12,
                  padding: '10px 12px',
                  background: 'var(--blue-lt)',
                  borderRadius: 10,
                  fontSize: 12,
                  color: 'var(--navy)',
                  lineHeight: 1.5,
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  gap: 10,
                }}
              >
                <span>This response reached the length limit. Click Continue for the rest.</span>
                <button
                  type="button"
                  className="ai-msg-btn"
                  onClick={() => onContinueAssistant()}
                  style={{ ...btnStyle, display: 'inline-flex', alignItems: 'center', gap: 4, color: 'var(--navy)', border: '1px solid var(--navy)' }}
                >
                  <CornerDownRight style={iconStyle} />
                  <span>Continue</span>
                </button>
              </div>
            )}
          {!isOffer && !isStreaming && !isRegenerating && (
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                gap: 6,
                marginTop: 10,
                paddingTop: 8,
                borderTop: '1px solid var(--bd2)',
              }}
            >
              <button
                type="button"
                className="ai-msg-btn"
                onClick={handleCopy}
                style={{ ...btnStyle, display: 'inline-flex', alignItems: 'center', gap: 4 }}
              >
                {copied ? <Check style={iconStyle} /> : <Copy style={iconStyle} />}
                <span>{copied ? 'Copied!' : 'Copy'}</span>
              </button>
              {onPlayMessage && (
                <button
                  type="button"
                  className="ai-msg-btn"
                  onClick={() => onPlayMessage(messageId, plainTextContent)}
                  style={{ ...btnStyle, display: 'inline-flex', alignItems: 'center', gap: 4 }}
                >
                  {isPlaying ? (
                    isPaused ? (
                      <Play style={iconStyle} />
                    ) : (
                      <Pause style={iconStyle} />
                    )
                  ) : (
                    <Play style={iconStyle} />
                  )}
                  <span>{isPlaying ? (isPaused ? 'Resume' : 'Pause') : 'Play'}</span>
                </button>
              )}
              {isPlaying && onStopPlayback && (
                <button
                  type="button"
                  className="ai-msg-btn"
                  onClick={onStopPlayback}
                  style={{ ...btnStyle, display: 'inline-flex', alignItems: 'center', gap: 4 }}
                >
                  <StopCircle style={iconStyle} />
                  <span>Stop</span>
                </button>
              )}
              {isLastAi && onRegenerate && (
                <button
                  type="button"
                  className="ai-msg-btn"
                  onClick={() => onRegenerate(messageId)}
                  style={{ ...btnStyle, display: 'inline-flex', alignItems: 'center', gap: 4 }}
                >
                  <RotateCcw style={iconStyle} />
                  <span>Regenerate</span>
                </button>
              )}
            </div>
          )}
        </div>
        {timestamp && (
          <span
            style={{
              display: 'block',
              fontSize: 10,
              color: 'var(--muted2)',
              marginTop: 4,
            }}
          >
            {formatChatDate(timestamp)}
          </span>
        )}
      </div>
    </div>
  )
}

function TypingMsg() {
  return (
    <div
      style={{
        display: 'flex',
        gap: 12,
        animation: 'msgIn .35s cubic-bezier(.16,1,.3,1) both',
      }}
    >
      <div
        style={{
          width: 30,
          height: 30,
          borderRadius: '50%',
          background: 'var(--navy)',
          fontFamily: "'Playfair Display',serif",
          fontSize: 14,
          color: 'var(--gold)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          marginTop: 2,
        }}
      >
        A
      </div>
      <div style={{ flex: 1 }}>
        <div
          style={{
            fontSize: 9.5,
            fontWeight: 500,
            letterSpacing: '1.5px',
            textTransform: 'uppercase',
            color: 'var(--muted2)',
            marginBottom: 5,
          }}
        >
          Athena · Beta
        </div>
        <div
          style={{
            background: 'var(--white)',
            border: '1px solid var(--bd)',
            borderRadius: 14,
            borderTopLeftRadius: 4,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 5,
            padding: '13px 17px',
          }}
        >
          {[0, 0.2, 0.4].map((d, i) => (
            <span
              key={i}
              style={{
                width: 6,
                height: 6,
                background: 'var(--blue)',
                borderRadius: '50%',
                opacity: 0.3,
                animation: `dot 1.2s ease-in-out ${d}s infinite`,
              }}
            />
          ))}
        </div>
      </div>
    </div>
  )
}

export default function MessageList({
  messages,
  isTyping = false,
  userInitials = 'U',
  playingMessageId = null,
  isPaused = false,
  onPlayMessage,
  onStopPlayback,
  onRegenerate,
  lastAiMessageId = null,
  lastUserMessageId = null,
  onEditUserMessage,
  regeneratingMessageId = null,
  onContinueAssistant,
  onSkillOfferAccurate,
  onSkillOfferQuick,
}: MessageListProps) {
  /** Only show the typing row while waiting for the first assistant token — not when an AI bubble (e.g. streaming) already exists */
  const lastRef = messages[messages.length - 1]
  const showTypingIndicator =
    isTyping &&
    lastRef !== undefined &&
    (lastRef.role === 'user' ||
      (lastRef.role === 'ai' && !!lastRef.skillOffer && lastRef.id !== 'streaming'))

  return (
    <div
      style={{
        padding: '24px 22px 16px',
        display: 'flex',
        flexDirection: 'column',
        gap: 20,
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: 700,
          margin: '0 auto',
          display: 'flex',
          flexDirection: 'column',
          gap: 20,
        }}
      >
        {messages.map((m) =>
          m.role === 'user' ? (
            <UserMsg
              key={m.id}
              id={m.id}
              text={m.text}
              files={m.files}
              initials={userInitials}
              timestamp={m.timestamp}
              isLastUserEditable={m.id === lastUserMessageId && !!onEditUserMessage}
              onEditUserMessage={onEditUserMessage}
            />
          ) : (
            <AIMsg
              key={m.id}
              id={m.id}
              data={m.data}
              isPlaying={playingMessageId === m.id}
              isPaused={isPaused}
              onPlayMessage={onPlayMessage}
              onStopPlayback={onStopPlayback}
              onRegenerate={onRegenerate}
              isLastAi={lastAiMessageId === m.id}
              timestamp={m.timestamp}
              isRegenerating={regeneratingMessageId === m.id && !m.data.b && m.data.pts.length === 0}
              isStreaming={m.id === 'streaming'}
              onContinueAssistant={onContinueAssistant}
              skillOffer={m.skillOffer}
              onSkillOfferAccurate={onSkillOfferAccurate}
              onSkillOfferQuick={onSkillOfferQuick}
            />
          )
        )}
        {showTypingIndicator && <TypingMsg />}
      </div>
    </div>
  )
}
